import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { AlertTriangle, ShieldAlert, TrendingUp, GraduationCap, Banknote, Clock, Sparkles } from "lucide-react";

const fmtTL = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".", ",")}M TL`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K TL`;
  return `${n} TL`;
};

const SEV_TONE = {
  high: "border-rose-300 bg-rose-50 text-rose-800",
  medium: "border-amber-300 bg-amber-50 text-amber-800",
  low: "border-emerald-300 bg-emerald-50 text-emerald-800",
};

export const RetentionPanel = ({ employeeId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.get(`/employees/${employeeId}/retention`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="card-surface p-5 text-sm text-slate-500">Risk analizi hazırlanıyor…</div>;
  if (!data) return null;

  const riskTone = data.risk_level === "high"
    ? "from-rose-100 to-rose-50 border-rose-300"
    : data.risk_level === "medium"
    ? "from-amber-100 to-amber-50 border-amber-300"
    : "from-emerald-100 to-emerald-50 border-emerald-300";

  return (
    <div className="space-y-5" data-testid={`retention-panel-${employeeId}`}>
      {/* Header */}
      <div className={`rounded-2xl border bg-gradient-to-br ${riskTone} p-5`}>
        <div className="flex items-center gap-4">
          <Avatar className="w-14 h-14 ring-2 ring-white">
            <AvatarImage src={data.avatar} />
            <AvatarFallback>{data.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display text-4xl font-semibold text-slate-900">{data.name}</h3>
              <ShieldAlert className="w-4 h-4 text-rose-600" />
            </div>
            <div className="text-xs text-slate-600">{data.title} · {data.department}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-4xl font-semibold text-rose-700">%{data.risk_score}</div>
            <div className="text-[0.65rem] text-slate-600 uppercase tracking-wider">ayrılma riski</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/60">
          <Stat label="Performans" value={`${data.performance_rating} ${data.performance_trend || ""}`} />
          <Stat label="Bağlılık" value={`${data.engagement_score}/100`} />
          <Stat label="Potansiyel" value={data.potential || "—"} className="capitalize" />
        </div>
      </div>

      {/* Triggers */}
      <div className="card-surface p-5" data-testid="retention-signals">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div className="label-eyebrow">Tetikleyici Sinyaller</div>
          <span className="ml-auto text-xs font-mono text-slate-500">{data.signals.length}</span>
        </div>
        <div className="space-y-2">
          {data.signals.map((s, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm ${SEV_TONE[s.severity] || SEV_TONE.medium}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
              <span className="font-medium flex-1">{s.label}</span>
              <span className="font-mono text-xs opacity-80">{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI recommendation */}
      <div className="rounded-2xl border border-sky-300 bg-gradient-to-br from-sky-50 to-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-sky-600 ai-pulse" />
          <span className="label-eyebrow text-sky-700">AI Önerisi: İki Kollu Aksiyon</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-sky-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-sky-700" />
              <span className="text-sm font-semibold text-slate-900">1. Terfi</span>
            </div>
            <p className="text-xs text-slate-700 leading-relaxed">
              Kıdemli seviyenin üzerinde performans. Kademe ilerlemesi <strong>tanınma</strong> ve <strong>ücret düzeltmesi</strong> sağlar.
            </p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-4 h-4 text-amber-700" />
              <span className="text-sm font-semibold text-slate-900">2. Çapraz Mentorluk</span>
            </div>
            <p className="text-xs text-slate-700 leading-relaxed">
              <strong>Risk Yönetimi</strong> alanına yeni ufuk. AI + risk kesişiminde stratejik yetenek.
            </p>
          </div>
        </div>
      </div>

      {/* Mentor card */}
      {data.mentor_recommendation && (
        <div className="card-surface p-5" data-testid="mentor-recommendation">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow">Önerilen Mentor</div>
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[0.65rem]">Müsait</Badge>
          </div>
          <div className="flex items-start gap-4">
            <Avatar className="w-14 h-14 ring-2 ring-sky-200">
              <AvatarImage src={data.mentor_recommendation.avatar} />
              <AvatarFallback>{data.mentor_recommendation.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-semibold text-slate-900">{data.mentor_recommendation.name}</div>
              <div className="text-xs text-slate-600 mb-2">{data.mentor_recommendation.title} · {data.mentor_recommendation.department}</div>
              <div className="text-xs text-slate-600">
                <Clock className="w-3 h-3 inline mr-1" /> Önerilen süre: <span className="text-slate-900 font-medium">{data.mentor_recommendation.recommended_months} ay</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-2xl font-semibold text-sky-700">%{data.mentor_recommendation.match_score}</div>
              <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">uyum</div>
            </div>
          </div>

          {data.mentor_recommendation.rationale?.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div className="label-eyebrow text-xs">Eşleşme Gerekçeleri</div>
              {data.mentor_recommendation.rationale.map((r, i) => (
                <div key={i} className="text-xs text-slate-700 flex gap-2">
                  <span className="text-sky-500 flex-shrink-0">→</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {data.mentor_recommendation.focus_areas?.length > 0 && (
            <div className="mt-4">
              <div className="label-eyebrow text-xs mb-2">Gelişim Odağı</div>
              <div className="flex flex-wrap gap-1.5">
                {data.mentor_recommendation.focus_areas.map((f) => (
                  <span key={f.id} className="evidence-chip">{f.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Replacement cost */}
      {data.replacement_cost && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4" data-testid="replacement-cost">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="w-4 h-4 text-amber-700" />
            <span className="label-eyebrow text-amber-800">Eğer Giderse: Yeniden Doldurma Maliyeti</span>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <div className="font-display text-4xl font-semibold text-slate-900">{fmtTL(data.replacement_cost.cost_tl)}</div>
              <div className="text-[0.7rem] text-slate-600">İşe alım + onboarding</div>
            </div>
            <div>
              <div className="font-display text-4xl font-semibold text-slate-900">~{data.replacement_cost.duration_months} ay</div>
              <div className="text-[0.7rem] text-slate-600">Yeniden işe alım + adaptasyon</div>
            </div>
          </div>
          <p className="text-xs text-amber-800/90 mt-3 leading-snug">
            AI/ML model hattı sahipsiz kalır. Elde tutmak insani tercih değil, <strong>operasyonel zorunluluk</strong>.
          </p>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, className = "" }) => (
  <div>
    <div className="text-[0.65rem] text-slate-600 uppercase tracking-wider">{label}</div>
    <div className={`font-mono text-sm font-semibold text-slate-900 ${className}`}>{value}</div>
  </div>
);

export default RetentionPanel;
